import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { JSONSchema7 } from 'json-schema';

import {
    AzavistaApiService, GroupedFieldsViewParentEntityType, IGroupedFieldsFieldNameViewFilter,
    IGroupedFieldsView, ITeamListItem
} from '@azavista/servicelib';
import { AzavistaSharedService, FieldComponentType, IAutocompleteInputFieldValue, IField, IValueChangedEventArgs } from '@azavista/components/shared';
import { IGroupedFieldsViewSection } from '@azavista/components/grouped-fields';
import { IFilterWithAvailableValues, IGroupedFieldsOrderComponentData, IGroupedFieldsOrderComponentResult, ISectionGroup } from './interfaces';
import { SharedService } from '../../shared.service';
import { AclService } from '../../acl.service';
import { RequiredScopesService } from '../../required-scopes.service';
import { SpecialFilterFieldId } from './enums';
import { GroupedFieldsViewService } from '../grouped-fields-view.service';

@Component({
    templateUrl: 'grouped-fields-order.component.html',
    styleUrls: ['grouped-fields-order.component.css'],
    standalone: false
})
export class GroupedFieldsOrderComponent implements OnInit {
    searchText = '';
    nameField: IField;
    nameFieldValue = '';
    teamField: IField;
    teamFieldValue: IAutocompleteInputFieldValue<ITeamListItem>;
    selectedTeams: ITeamListItem[];
    selectedTeam: ITeamListItem;
    allTeams: ITeamListItem[];
    selectAndMultiSelectFields: IField[];
    currentView: IGroupedFieldsView;
    isNew = false;
    availableFields: IField[];
    sectionGroups: ISectionGroup[];
    canSave = false;
    loaded = false;
    isAdmin = false;
    filtersWithAvailableValues: IFilterWithAvailableValues[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: IGroupedFieldsOrderComponentData,
        private matDialogRef: MatDialogRef<GroupedFieldsOrderComponent, IGroupedFieldsOrderComponentResult>,
        private cmpSharedSvc: AzavistaSharedService, private sharedSvc: SharedService,
        private apiSvc: AzavistaApiService, private aclSvc: AclService, private rsSvc: RequiredScopesService,
        private gfvSvc: GroupedFieldsViewService
    ) {
    }

    async ngOnInit(): Promise<void> {
        this.allTeams = await this.apiSvc.getAllTeams();
        this.isNew = !this.data.currentView;
        this.currentView = this.sharedSvc.clone(this.data.currentView || this.createNewGroupedFieldsView());
        this.adjustFilters();

        this.canSave = this.getCanSave();
        this.nameField = this.createNameField();
        const filterFields = [...this.data.filterFields];
        if (this.data.parentEntityType === GroupedFieldsViewParentEntityType.event) {
            filterFields.push(this.gfvSvc.getEventSettingsPublishField());
        }
        this.selectAndMultiSelectFields = this.sharedSvc.getSelectAndMultiSelectFields(filterFields);
        this.teamField = this.createTeamField();
        this.teamFieldValue = this.createTeamFieldValue();
        this.selectedTeams = this.getSelectedTeams() || [];
        this.sectionGroups = this.createSectionGroups(this.currentView.sections, this.data.allFields);
        this.availableFields = this.getAvailableFields(this.sectionGroups, this.data.allFields);
        this.sharedSvc.sortAlphabetically(this.availableFields, x => x.label);
        this.loaded = true;
        this.isAdmin = this.aclSvc.isAdmin();
        this.filtersWithAvailableValues = this.createFiltersWithAvailableValues();
    }

    createFiltersWithAvailableValues(): IFilterWithAvailableValues[] {
        const result: IFilterWithAvailableValues[] = [];
        for (const filter of this.currentView.filters) {
            result.push({ filter: filter, availableValues: this.getAvailableValuesForField(filter.field_name) });
        }
        return result;
    }

    getAvailableValuesForField(fieldId: string): string[] {
        if (!fieldId) {
            return [];
        }
        if (fieldId === SpecialFilterFieldId.eventSettingsPublish) {
            return this.gfvSvc.getEventSettingsPublishField().schema.enum as string[];
        }
        const field = this.selectAndMultiSelectFields.find(x => x.id === fieldId);
        const isEnum = this.cmpSharedSvc.isFieldEnum(field.schema);
        let result: string[];
        if (isEnum) {
            result = field.schema.enum as string[];
        } else {
            result = (field.schema.items as JSONSchema7)?.enum as string[];
        }
        return result || [];
    }

    adjustFilters(): void {
        // If the filter comes as null from the server - create it with default values
        if (!this.currentView.filters || this.currentView.filters.length === 0) {
            this.currentView.filters = this.createDefaultFilters();
        }
        // Event must have two filters
        if (this.data.parentEntityType === GroupedFieldsViewParentEntityType.event && this.currentView.filters.length === 1) {
            // Only one item - add one more
            this.currentView.filters.push({ field_name: null, values: [] });
        }
    }

    get connectedDropLists(): string[] {
        const dropLists = ['available-fields-list'];
        this.sectionGroups.forEach((section, i) => {
            dropLists.push('section-' + i + '-left-fields');
            dropLists.push('section-' + i + '-right-fields');
        });
        return dropLists;
    }

    onSectionDropped(event: CdkDragDrop<ISectionGroup[]>): void {
        if (event.previousContainer === event.container) {
            moveItemInArray(
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
        }
    }

    onFieldDropped(event: CdkDragDrop<string[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
        }
    }

    onViewNameChanged(change: IValueChangedEventArgs): void {
        this.currentView.name = change.value;
        this.canSave = this.getCanSave();
    }

    onMultiSelectFieldChanged(change: MatSelectChange, fwav: IFilterWithAvailableValues): void {
        const fieldId = change.value as string;
        if (!fieldId) {
            fwav.availableValues = [];
            fwav.filter.values = [];
            return;
        }
        fwav.availableValues = this.getAvailableValuesForField(fwav.filter.field_name);
    }

    onNewSectionAdded(): void {
        this.sectionGroups.push(this.createDefaultSectionGroup());
    }

    onSectionDeleted(sectionGroup: ISectionGroup): void {
        const index = this.sectionGroups.findIndex(x => x === sectionGroup);
        this.sectionGroups.splice(index, 1);
    }

    onTeamValueChanged(change: IValueChangedEventArgs): void {
        this.selectedTeam = change.value as ITeamListItem;
    }

    onAddTeamClicked(): void {
        if (!this.selectedTeam) {
            return;
        }
        if (this.selectedTeams.find(x => x.id === this.selectedTeam.id)) {
            return;
        }
        this.selectedTeams.push(this.selectedTeam);
    }

    onRemoveTeamClicked(team: ITeamListItem): void {
        const index = this.selectedTeams.findIndex(x => x.id === team.id);
        this.selectedTeams.splice(index, 1);
    }

    onClose(): void {
        this.matDialogRef.close();
    }

    onSave(): void {
        this.currentView.sections = [];
        for (const section of this.sectionGroups) {
            let leftColumn: string[];
            let rightColumn: string[];
            if (this.data.useAvailableFieldsNames) {
                leftColumn = section.leftFields.map(x => x.name);
                rightColumn = section.rightFields.map(x => x.name);
            } else {
                leftColumn = section.leftFields.map(x => x.id);
                rightColumn = section.rightFields.map(x => x.id);
            }
            this.currentView.sections.push({
                label: section.label,
                left_column: leftColumn,
                right_column: rightColumn,
                read_only: section.readonly
            });
        }
        this.currentView.team_ids = this.selectedTeams.map(x => x.id);
        this.matDialogRef.close({ view: this.currentView, isNew: this.isNew, isDeleted: false });
    }

    onDelete(): void {
        this.matDialogRef.close({ view: this.currentView, isNew: this.isNew, isDeleted: true });
    }

    getCanSave(): boolean {
        return !this.cmpSharedSvc.isNullOrWhitespace(this.currentView.name);
    }

    getSelectedTeams(): ITeamListItem[] {
        const filteredTeams = this.allTeams.filter(x => this.currentView.team_ids.indexOf(x.id) >= 0);
        return filteredTeams;
    }

    shouldFilterByFilter(field: IField): boolean {
        const trimmedAndUpperCasedSearchText = this.searchText.trim().toUpperCase();
        return field.label.toUpperCase().indexOf(trimmedAndUpperCasedSearchText) === -1;
    }

    getAvailableFields(sectionGroups: ISectionGroup[], allFields: IField[]): IField[] {
        // Available fields are all fields without already used fields in sections
        const availableFields: IField[] = [...allFields];
        for (const sectionGroup of sectionGroups) {
            for (const field of sectionGroup.leftFields) {
                let index: number;
                if (this.data.useAvailableFieldsNames) {
                    index = availableFields.findIndex(x => x.name === field.name);
                } else {
                    index = availableFields.findIndex(x => x.id === field.id);
                }
                availableFields.splice(index, 1);
            }
            for (const field of sectionGroup.rightFields) {
                let index: number;
                if (this.data.useAvailableFieldsNames) {
                    index = availableFields.findIndex(x => x.name === field.name);
                } else {
                    index = availableFields.findIndex(x => x.id === field.id);
                }
                availableFields.splice(index, 1);
            }
        }
        return availableFields;
    }

    createSectionGroups(sections: IGroupedFieldsViewSection[], allFields: IField[]): ISectionGroup[] {
        if (sections.length === 0) {
            return [this.createDefaultSectionGroup()];
        }

        const sectionGroups: ISectionGroup[] = [];
        for (const section of sections) {
            let leftFields: IField[];
            let rightFields: IField[];
            if (this.data.useAvailableFieldsNames) {
                leftFields = section.left_column?.map(x => allFields.find(y => y.name === x)).filter(x => x) || [];
                rightFields = section.right_column?.map(x => allFields.find(y => y.name === x)).filter(x => x) || [];
            } else {
                leftFields = section.left_column?.map(x => allFields.find(y => y.id === x)).filter(x => x) || [];
                rightFields = section.right_column?.map(x => allFields.find(y => y.id === x)).filter(x => x) || [];
            }
            sectionGroups.push({
                label: section.label,
                leftFields: leftFields,
                rightFields: rightFields,
                readonly: section.read_only
            });
        }
        return sectionGroups;
    }

    createDefaultSectionGroup(): ISectionGroup {
        const sectionGroup: ISectionGroup = {
            label: '', leftFields: [], rightFields: [], readonly: false
        };
        return sectionGroup;
    }

    createNameField(): IField {
        const field = {
            id: 'name', name: 'name', editable: true, mandatory_for_planners: true,
            schema: { type: 'string' }
        } as IField;
        return field;
    }

    createTeamField(): IField {
        const field = {
            id: 'team', name: 'team', editable: true,
            schema: { type: 'object' }, component: FieldComponentType.autocomplete
        } as IField;
        return field;
    }

    createTeamFieldValue(): IAutocompleteInputFieldValue {
        const items = this.allTeams || [];
        const value: IAutocompleteInputFieldValue = {
            displayProperty: 'name', items: items, selected: null, filteredItems: items
        };
        return value;
    }

    createNewGroupedFieldsView(): IGroupedFieldsView {
        const result: IGroupedFieldsView = {
            id: '',
            name: '',
            global_fields: this.data.useViewInAllEvents,
            sections: [],
            shared_view: false,
            team_ids: [],
            view_by_all: true,
            // filter: defaultFilter,
            filters: this.createDefaultFilters()
        };
        return result;
    }

    // createDefaultFilter(): IGroupedFieldsViewFilter {
    //     const filter: IGroupedFieldsViewFilter = { field_id: null, values: [] };
    //     return filter;
    // }

    createDefaultFilters(): IGroupedFieldsFieldNameViewFilter[] {
        const filters: IGroupedFieldsFieldNameViewFilter[] = [
            { field_name: null, values: [] }
        ];
        return filters;
    }
}
